import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './SearchControl.css';
import apiService from '../services/apiService';
import { buildPopupContent, buildAddressPopupContent } from '../utils/popupBuilder';

const getFeatureText = (feature) => {
  const props = feature.properties || {};
  return [
    props.name,
    props.operator,
    props.address,
    props['addr:street'],
    props['addr:housenumber'],
    props['addr:city'],
    props['addr:postcode'],
    props.healthcare,
    props['healthcare:speciality'],
    props.healthcare_type,
    props.specialty,
    props.name && props.name.replace(/\s+/g, ' ')
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const normalizeSearchText = (text) => text.trim().toLowerCase();

const buildResultLabel = (feature) => {
  const props = feature.properties || {};
  const parts = [];
  if (props.name) parts.push(props.name);
  if (props.healthcare || props['healthcare:speciality'] || props.specialty) {
    parts.push(props.healthcare || props['healthcare:speciality'] || props.specialty);
  }
  if (props['addr:street'] || props['addr:city']) {
    const addressParts = [];
    if (props['addr:street']) addressParts.push(props['addr:street']);
    if (props['addr:housenumber']) addressParts.push(props['addr:housenumber']);
    if (props['addr:city']) addressParts.push(props['addr:city']);
    parts.push(addressParts.join(' '));
  }
  return parts.join(' · ') || props.name || 'Unbekannter Eintrag';
};

const SearchControl = ({ geoJsonData, activeLayers, layerConfig, loadGeoJsonLayer }) => {
  const map = useMap();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [marker, setMarker] = useState(null);
  const debounceTimer = useRef(null);

  const doctorLayerMatcher = useMemo(() => /arzt|ärzte|mvz/i, []);

  const candidateLayerNames = useMemo(() => {
    const knownLayers = Object.keys(layerConfig || {}).filter(name => doctorLayerMatcher.test(name));
    const activeDoctorLayers = Object.keys(activeLayers || {}).filter(name => doctorLayerMatcher.test(name));
    const loadedDoctorLayers = Object.keys(geoJsonData || {}).filter(name => doctorLayerMatcher.test(name));

    return Array.from(new Set([...knownLayers, ...activeDoctorLayers, ...loadedDoctorLayers]));
  }, [activeLayers, geoJsonData, layerConfig, doctorLayerMatcher]);

  const performSearch = useCallback(async (query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return;

    setIsSearching(true);

    try {
      const loadedLayers = { ...geoJsonData };
      const missingLayers = candidateLayerNames.filter(name => !loadedLayers[name]);

      if (missingLayers.length > 0 && loadGeoJsonLayer) {
        await Promise.all(missingLayers.map(async (layerName) => {
          try {
            const loaded = await loadGeoJsonLayer(layerName);
            if (loaded) {
              loadedLayers[layerName] = loaded;
            }
          } catch (error) {
            console.error(`Fehler beim Laden des Layers ${layerName}:`, error);
          }
        }));
      }

      const localResults = [];

      Object.entries(loadedLayers).forEach(([layerName, layerInfo]) => {
        if (!doctorLayerMatcher.test(layerName)) return;
        if (!layerInfo?.data?.features) return;

        layerInfo.data.features.forEach((feature) => {
          if (feature.geometry?.type !== 'Point') return;

          const text = getFeatureText(feature);
          if (!text.includes(normalizedQuery)) return;

          const [lng, lat] = feature.geometry.coordinates;
          const type = /mvz/i.test(layerName) || (feature.properties?.healthcare_type || '').toLowerCase() === 'mvz' ? 'mvz' : 'doctor';

          localResults.push({
            source: 'layer',
            layerName,
            feature,
            lat,
            lng,
            type,
            label: buildResultLabel(feature)
          });
        });
      });

      const apiResults = [];
      try {
        const data = await apiService.searchLocation(query);
        if (data?.results?.length) {
          data.results.slice(0, 50).forEach((result) => {
            if (result.latitude == null || result.longitude == null) return;
            apiResults.push({
              source: 'address',
              label: result.displayName || result.display_name || result.name || 'Adresse',
              lat: result.latitude,
              lng: result.longitude,
              properties: result.properties || {}
            });
          });
        }
      } catch (searchError) {
        console.error('API address search failed:', searchError);
      }

      setSearchResults([...localResults, ...apiResults].slice(0, 50));
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [geoJsonData, candidateLayerNames, loadGeoJsonLayer, doctorLayerMatcher]);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, performSearch]);

  const handleResultClick = (result) => {
    const { lat, lng, feature, source, label, properties } = result;

    if (marker) {
      map.removeLayer(marker);
    }

    const newMarker = L.marker([lat, lng], {
      icon: L.icon({
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      })
    }).addTo(map);

    let popupContent;

    if (source === 'address') {
      popupContent = buildAddressPopupContent(label, properties);
    } else {
      popupContent = buildPopupContent(feature.properties || {});
    }

    newMarker.bindPopup(popupContent, {
      maxWidth: 360,
      minWidth: 280,
      className: 'custom-popup'
    }).openPopup();

    map.setView([lat, lng], 15);
    setMarker(newMarker);
    setSearchResults([]);
    setSearchQuery('');
  };

  return (
    <div
      className="search-control"
      onWheelCapture={(e) => searchResults.length > 0 && e.stopPropagation()}
      onTouchMoveCapture={(e) => searchResults.length > 0 && e.stopPropagation()}
    >
      <div className="search-form">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Ort, Arzt oder MVZ suchen (mind. 3 Zeichen)..."
          className="search-input"
        />
        {isSearching && <span className="search-spinner">🔍</span>}
      </div>
      
      {searchResults.length > 0 && (
        <div
          className="search-results"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {searchResults.map((result, index) => (
            <div
              key={`${result.source}-${result.layerName || 'address'}-${index}`}
              className="search-result-item"
              onClick={() => handleResultClick(result)}
            >
              {result.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchControl;
